import {
  API_PREFIX,
  apiErrorEnvelopeSchema,
  type ErrorCode,
  TENANT_HEADER,
} from '@rw/contracts/api';
import { publicEnv } from '../env';
import { getActiveTenantId } from './active-tenant';

let sessionRefresh: Promise<string | null> | null = null;
let rateLimitedUntil = 0;

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterMs: number | null,
    readonly code: ErrorCode | null = null
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function retryAfterMs(response: Response): number | null {
  const value = response.headers.get('retry-after');
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1_000, 60_000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.min(Math.max(date - Date.now(), 0), 60_000) : null;
}

async function waitForRateLimit(signal?: AbortSignal | null): Promise<void> {
  const waitMs = rateLimitedUntil - Date.now();
  if (waitMs <= 0) return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, waitMs);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(signal.reason ?? new DOMException('The operation was aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

async function refreshAccessToken(): Promise<string | null> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.auth.refreshSession();
  return error ? null : (data.session?.access_token ?? null);
}

async function clearLocalSession(): Promise<void> {
  const { supabase } = await import('./supabase');
  await supabase.auth.signOut({ scope: 'local' });
}

function activeTenantId(): string | undefined {
  return (
    getActiveTenantId() ??
    (typeof window === 'undefined'
      ? undefined
      : window.location.pathname.match(/\/app\/tenants\/([^/]+)/)?.[1])
  );
}

async function authenticatedApiResponse(
  path: string,
  accessToken: string,
  init?: RequestInit
): Promise<Response> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const tenantId = activeTenantId();
  const request = async (token: string) => {
    await waitForRateLimit(init?.signal);
    return fetch(`${publicEnv.VITE_API_URL}${API_PREFIX}${normalizedPath}`, {
      ...init,
      headers: {
        authorization: `Bearer ${token}`,
        ...(tenantId ? { [TENANT_HEADER]: tenantId } : {}),
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  };
  let response = await request(accessToken);

  if (response.status === 429) {
    const delay = retryAfterMs(response) ?? 1_000;
    rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + delay);
    const method = init?.method?.toUpperCase() ?? 'GET';
    if (method === 'GET' || method === 'HEAD') {
      await waitForRateLimit(init?.signal);
      response = await request(accessToken);
    }
  }

  if (response.status === 401) {
    sessionRefresh ??= refreshAccessToken().finally(() => {
      sessionRefresh = null;
    });
    const refreshedToken = await sessionRefresh;
    if (refreshedToken) response = await request(refreshedToken);
    if (response.status === 401) await clearLocalSession();
  }

  if (!response.ok) {
    const delay = response.status === 429 ? retryAfterMs(response) : null;
    if (response.status === 429) {
      rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + (delay ?? 1_000));
    }
    const body = apiErrorEnvelopeSchema.safeParse(await response.json().catch(() => null));
    throw new ApiRequestError(
      body.success ? body.data.error.message : `Request failed with ${response.status}`,
      response.status,
      delay,
      body.success ? body.data.error.code : null
    );
  }
  return response;
}

export async function apiRequest<T>(
  path: string,
  accessToken: string,
  init?: RequestInit
): Promise<T> {
  const response = await authenticatedApiResponse(path, accessToken, init);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function publicApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  await waitForRateLimit(init?.signal);
  const response = await fetch(`${publicEnv.VITE_API_URL}${API_PREFIX}${normalizedPath}`, {
    ...init,
    cache: 'no-store',
    credentials: 'omit',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const delay = response.status === 429 ? retryAfterMs(response) : null;
    if (response.status === 429) {
      rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + (delay ?? 1_000));
    }
    const body = apiErrorEnvelopeSchema.safeParse(await response.json().catch(() => null));
    throw new ApiRequestError(
      body.success ? body.data.error.message : `Request failed with ${response.status}`,
      response.status,
      delay,
      body.success ? body.data.error.code : null
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function apiBlobRequest(
  path: string,
  accessToken: string,
  init?: RequestInit
): Promise<Blob> {
  const response = await authenticatedApiResponse(path, accessToken, init);
  return response.blob();
}
